import React, { useState, useMemo, useCallback } from 'react';
import {
  Container, Card, Button, Alert, Row, Col, Badge, Table,
  Spinner, Modal, Form, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import {
  useDatasets, useValidateCSV, useCreateDataset, useUploadValues, useDeleteDataset,
} from '../hooks/useDatasets';
import { useBuildings } from '../hooks/useBuildings';
import { useCommunity } from '../hooks/useCommunities';
import { useToast } from '../components/ToastNotification';
import { communityDatasetTypes, buildingDatasetTypes } from '../data/dummyData';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';

/**
 * DatasetUploadPage - Standalone Data Management page for a community.
 *
 * Two sections:
 *   1. Community Datasets (PV generation, wind generation, tariffs)
 *   2. Building Datasets (load profiles, PV generation, energy bills) grouped by building
 *
 * Upload flow:
 *   1. User clicks "Upload" for a dataset type
 *   2. Modal opens: select file, optional building (for building datasets)
 *   3. Validate CSV via backend POST /api/datasets/upload
 *   4. If valid, confirm to save:
 *      a. Create dataset record POST /api/datasets
 *      b. Upload values POST /api/datasets/:id/values
 *   5. Toast notification on success/failure
 */
const DatasetUploadPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { id: communityId } = useParams();
  const { canEdit } = useAuth();
  const { t } = useTranslation();

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------
  const { data: community, isLoading: communityLoading } = useCommunity(communityId);
  const { data: datasets = [], isLoading: datasetsLoading, refetch: refetchDatasets } = useDatasets(
    communityId ? { community_id: communityId } : {}
  );
  const { data: buildings = [], isLoading: buildingsLoading } = useBuildings(communityId);

  // -----------------------------------------------------------------------
  // Mutations
  // -----------------------------------------------------------------------
  const validateMutation = useValidateCSV();
  const createMutation = useCreateDataset();
  const uploadMutation = useUploadValues();
  const deleteMutation = useDeleteDataset();

  // -----------------------------------------------------------------------
  // Upload modal state
  // -----------------------------------------------------------------------
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadSection, setUploadSection] = useState(null); // 'community' | 'building'
  const [selectedType, setSelectedType] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [validationReport, setValidationReport] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // -----------------------------------------------------------------------
  // Derived data
  // -----------------------------------------------------------------------
  const communityDatasets = useMemo(
    () => datasets.filter((d) => d.building_id === null || d.building_id === undefined),
    [datasets]
  );

  const buildingDatasets = useMemo(
    () => datasets.filter((d) => d.building_id !== null && d.building_id !== undefined),
    [datasets]
  );

  /** Group building datasets by building_id */
  const buildingDatasetsByBuilding = useMemo(() => {
    const map = {};
    for (const d of buildingDatasets) {
      if (!map[d.building_id]) map[d.building_id] = [];
      map[d.building_id].push(d);
    }
    return map;
  }, [buildingDatasets]);

  /** Map building UUID -> building object for quick lookup */
  const buildingMap = useMemo(() => {
    const map = {};
    for (const b of buildings) {
      map[b.id] = b;
    }
    return map;
  }, [buildings]);

  // -----------------------------------------------------------------------
  // Upload modal helpers
  // -----------------------------------------------------------------------
  const openUploadModal = (section) => {
    setUploadSection(section);
    setSelectedType('');
    setSelectedBuildingId('');
    setUploadedFile(null);
    setValidationReport(null);
    setIsValidating(false);
    setIsSaving(false);
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadSection(null);
    setSelectedType('');
    setSelectedBuildingId('');
    setUploadedFile(null);
    setValidationReport(null);
    setIsValidating(false);
    setIsSaving(false);
  };

  const availableTypes = uploadSection === 'community'
    ? communityDatasetTypes
    : buildingDatasetTypes;

  // -----------------------------------------------------------------------
  // Dropzone
  // -----------------------------------------------------------------------
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0]);
      setValidationReport(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv'],
      'text/plain': ['.csv'],
    },
    multiple: false,
    noClick: false,
    noKeyboard: false,
  });

  // -----------------------------------------------------------------------
  // Validate CSV via backend
  // -----------------------------------------------------------------------
  const handleValidate = async () => {
    if (!uploadedFile || !selectedType) return;

    setIsValidating(true);
    setValidationReport(null);

    try {
      const report = await validateMutation.mutateAsync({
        file: uploadedFile,
        datasetType: selectedType,
      });
      setValidationReport(report);
    } catch (err) {
      addToast(t('dataset_upload.toast_validation_failed', { error: err.message }), 'danger');
    } finally {
      setIsValidating(false);
    }
  };

  // -----------------------------------------------------------------------
  // Save dataset (create record + upload values)
  // -----------------------------------------------------------------------
  const handleSave = async () => {
    if (!uploadedFile || !selectedType || !communityId) return;

    // For building datasets, building must be selected
    if (uploadSection === 'building' && !selectedBuildingId) {
      addToast(t('dataset_upload.toast_select_building'), 'warning');
      return;
    }

    setIsSaving(true);

    try {
      const isDayAhead = selectedType.startsWith('day_ahead_');

      // For day-ahead datasets, append to existing dataset if one exists
      let datasetRecord;
      if (isDayAhead) {
        const existing = communityDatasets.find(d => d.dataset_type === selectedType);
        if (existing) {
          datasetRecord = existing;
        }
      }

      // Create new dataset record if needed
      if (!datasetRecord) {
        const createPayload = {
          community_id: communityId,
          name: uploadedFile.name.replace(/\.csv$/i, ''),
          dataset_type: selectedType,
        };
        if (uploadSection === 'building' && selectedBuildingId) {
          createPayload.building_id = selectedBuildingId;
        }
        datasetRecord = await createMutation.mutateAsync(createPayload);
      }

      // Upload values (appends to existing for day-ahead)
      await uploadMutation.mutateAsync({
        datasetId: datasetRecord.id,
        file: uploadedFile,
      });

      const wasUpdate = isDayAhead && communityDatasets.find(d => d.dataset_type === selectedType);
      const toastKey = wasUpdate ? 'dataset_upload.toast_update_success' : 'dataset_upload.toast_upload_success';
      addToast(t(toastKey, { name: datasetRecord.name }), 'success');
      closeUploadModal();
    } catch (err) {
      addToast(t('dataset_upload.toast_upload_failed', { error: err.message }), 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Delete dataset
  // -----------------------------------------------------------------------
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      addToast(t('dataset_upload.toast_delete_success', { name: deleteTarget.name }), 'success');
      setDeleteTarget(null);
    } catch (err) {
      addToast(t('dataset_upload.toast_delete_failed', { error: err.message }), 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Formatting helpers
  // -----------------------------------------------------------------------
  const formatDatasetType = (dsType) => {
    if (!dsType) return t('common.n_a');
    return dsType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDateRange = (ds) => {
    const start = ds.time_start;
    const end = ds.time_end;
    // Filter out placeholder dates (year 2000)
    if (!start || !end) return '--';
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (startDate.getFullYear() <= 2000) return t('dataset_upload.no_data_yet');
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  };

  const getRowCount = (ds) => {
    if (ds.quality && ds.quality.row_count) return ds.quality.row_count.toLocaleString();
    return '--';
  };

  const getResolutionBadge = (resolution) => {
    const map = {
      'unknown': { bg: 'secondary', text: t('dataset_upload.status_unknown') },
      '15min': { bg: 'info', text: t('dataset_upload.res_15min') },
      '15-min': { bg: 'info', text: t('dataset_upload.res_15min') },
      '30min': { bg: 'info', text: t('dataset_upload.res_30min') },
      '30-min': { bg: 'info', text: t('dataset_upload.res_30min') },
      'hourly': { bg: 'primary', text: t('dataset_upload.res_hourly') },
      'daily': { bg: 'secondary', text: t('dataset_upload.res_daily') },
      'monthly': { bg: 'dark', text: t('dataset_upload.res_monthly') },
    };
    return map[resolution] || { bg: 'secondary', text: resolution || t('common.n_a') };
  };

  const getStatusBadge = (ds) => {
    const hasData = ds.quality && ds.quality.row_count && ds.quality.row_count > 0;
    if (hasData) return { bg: 'success', text: t('dataset_upload.status_active') };
    return { bg: 'warning', text: t('dataset_upload.status_empty') };
  };

  const getTypeIcon = (dsType) => {
    const allTypes = [...communityDatasetTypes, ...buildingDatasetTypes];
    const found = allTypes.find((dt) => dt.type === dsType);
    return found ? found.icon : '📄';
  };

  // -----------------------------------------------------------------------
  // Guard: no communityId
  // -----------------------------------------------------------------------
  if (!communityId) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">
          <Alert.Heading>{t('dataset_upload.no_community_title')}</Alert.Heading>
          <p>{t('dataset_upload.no_community_text')}</p>
          <Button variant="primary" onClick={() => navigate('/communities/new')}>
            {t('dataset_upload.create_community_btn')}
          </Button>
        </Alert>
      </Container>
    );
  }

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------
  const isLoading = communityLoading || datasetsLoading || buildingsLoading;

  // -----------------------------------------------------------------------
  // Render: Dataset table row
  // -----------------------------------------------------------------------
  const renderDatasetRow = (ds) => {
    const resBadge = getResolutionBadge(ds.resolution);
    const statusBadge = getStatusBadge(ds);
    return (
      <tr key={ds.id}>
        <td>
          <span className="me-2">{getTypeIcon(ds.dataset_type)}</span>
          <span className="fw-semibold">{ds.name}</span>
        </td>
        <td>
          <Badge bg="light" text="dark" className="border">
            {formatDatasetType(ds.dataset_type)}
          </Badge>
        </td>
        <td className="small text-muted">{formatDateRange(ds)}</td>
        <td>
          <Badge bg={resBadge.bg}>{resBadge.text}</Badge>
        </td>
        <td className="text-end">{getRowCount(ds)}</td>
        <td>
          <Badge bg={statusBadge.bg}>{statusBadge.text}</Badge>
        </td>
        <td className="text-end">
          {canEdit && (
            <OverlayTrigger placement="top" overlay={<Tooltip>{t('dataset_upload.tooltip_delete')}</Tooltip>}>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setDeleteTarget(ds)}
              >
                {t('dataset_upload.btn_delete')}
              </Button>
            </OverlayTrigger>
          )}
        </td>
      </tr>
    );
  };

  // -----------------------------------------------------------------------
  // Render: main
  // -----------------------------------------------------------------------
  return (
    <Container fluid className="py-4">
      {/* ================================================================= */}
      {/* Header                                                             */}
      {/* ================================================================= */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{ color: theme.colors.primary.dark }}>
            {t('dataset_upload.header')}
          </h2>
          <p className="text-muted mb-0">
            {community?.name
              ? t('dataset_upload.subtitle_named', { name: community.name })
              : t('dataset_upload.subtitle_loading')}
          </p>
        </div>
        <Button
          variant="outline-secondary"
          onClick={() => navigate('/communities')}
        >
          {t('dataset_upload.back_to_communities')}
        </Button>
      </div>

      {isLoading && (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: theme.colors.primary.main }} />
          <div className="mt-2 text-muted">{t('dataset_upload.loading_data')}</div>
        </div>
      )}

      {!isLoading && (
        <>
          {/* ============================================================= */}
          {/* Section 1: Community Datasets                                   */}
          {/* ============================================================= */}
          <Card className="mb-4 shadow-sm">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ backgroundColor: theme.colors.primary.pale }}
            >
              <h5 className="mb-0" style={{ color: theme.colors.primary.dark }}>
                {t('dataset_upload.community_datasets')}
                <Badge bg="secondary" className="ms-2">
                  {communityDatasets.length}
                </Badge>
              </h5>
              {canEdit && (
                <Button
                  variant="primary"
                  size="sm"
                  style={{ backgroundColor: theme.colors.primary.main, borderColor: theme.colors.primary.main }}
                  onClick={() => openUploadModal('community')}
                >
                  {t('dataset_upload.upload_community_dataset')}
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {communityDatasets.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-2">
                    {t('dataset_upload.no_community_datasets')}
                  </p>
                  <p className="small text-muted mb-0">
                    {t('dataset_upload.community_dataset_hint')}
                  </p>
                </div>
              ) : (
                <Table hover responsive className="mb-0">
                  <thead style={{ backgroundColor: theme.colors.neutral[100] }}>
                    <tr>
                      <th>{t('dataset_upload.col_name')}</th>
                      <th>{t('dataset_upload.col_type')}</th>
                      <th>{t('dataset_upload.col_date_range')}</th>
                      <th>{t('dataset_upload.col_resolution')}</th>
                      <th className="text-end">{t('dataset_upload.col_rows')}</th>
                      <th>{t('dataset_upload.col_status')}</th>
                      <th className="text-end">{t('dataset_upload.col_actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {communityDatasets.map(renderDatasetRow)}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {/* ============================================================= */}
          {/* Section 2: Building Datasets                                    */}
          {/* ============================================================= */}
          <Card className="mb-4 shadow-sm">
            <Card.Header
              className="d-flex justify-content-between align-items-center"
              style={{ backgroundColor: theme.colors.primary.pale }}
            >
              <h5 className="mb-0" style={{ color: theme.colors.primary.dark }}>
                {t('dataset_upload.building_datasets')}
                <Badge bg="secondary" className="ms-2">
                  {buildingDatasets.length}
                </Badge>
              </h5>
              {buildings.length > 0 && canEdit && (
                <Button
                  variant="primary"
                  size="sm"
                  style={{ backgroundColor: theme.colors.primary.main, borderColor: theme.colors.primary.main }}
                  onClick={() => openUploadModal('building')}
                >
                  {t('dataset_upload.upload_building_dataset')}
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {buildings.length === 0 ? (
                <div className="text-center py-4">
                  <Alert variant="info" className="mb-0 mx-3">
                    <strong>{t('dataset_upload.no_buildings_defined')}</strong>{' '}
                    {t('dataset_upload.no_buildings_hint')}
                    <div className="mt-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigate(`/communities/${communityId}/buildings`)}
                      >
                        {t('dataset_upload.go_to_buildings')}
                      </Button>
                    </div>
                  </Alert>
                </div>
              ) : buildingDatasets.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-2">
                    {t('dataset_upload.no_building_datasets')}
                  </p>
                  <p className="small text-muted mb-0">
                    {t('dataset_upload.building_dataset_hint')}
                  </p>
                </div>
              ) : (
                <>
                  {/* Render datasets grouped by building */}
                  {buildings.map((building) => {
                    const bDatasets = buildingDatasetsByBuilding[building.id] || [];
                    if (bDatasets.length === 0) return null;
                    return (
                      <div key={building.id}>
                        <div
                          className="px-3 py-2 fw-semibold small"
                          style={{
                            backgroundColor: theme.colors.neutral[50],
                            borderTop: `1px solid ${theme.colors.neutral[200]}`,
                            borderBottom: `1px solid ${theme.colors.neutral[200]}`,
                            color: theme.colors.neutral[700],
                          }}
                        >
                          {building.building_id}
                          <Badge bg="light" text="dark" className="ms-2 border">
                            {building.building_type}
                          </Badge>
                        </div>
                        <Table hover responsive className="mb-0">
                          <thead style={{ backgroundColor: theme.colors.neutral[100] }}>
                            <tr>
                              <th>{t('dataset_upload.col_name')}</th>
                              <th>{t('dataset_upload.col_type')}</th>
                              <th>{t('dataset_upload.col_date_range')}</th>
                              <th>{t('dataset_upload.col_resolution')}</th>
                              <th className="text-end">{t('dataset_upload.col_rows')}</th>
                              <th>{t('dataset_upload.col_status')}</th>
                              <th className="text-end">{t('dataset_upload.col_actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bDatasets.map(renderDatasetRow)}
                          </tbody>
                        </Table>
                      </div>
                    );
                  })}

                  {/* Show any building datasets whose building is not in the list */}
                  {Object.keys(buildingDatasetsByBuilding)
                    .filter((bid) => !buildingMap[bid])
                    .map((bid) => {
                      const orphans = buildingDatasetsByBuilding[bid];
                      if (!orphans || orphans.length === 0) return null;
                      return (
                        <div key={bid}>
                          <div
                            className="px-3 py-2 fw-semibold small"
                            style={{
                              backgroundColor: theme.colors.neutral[50],
                              borderTop: `1px solid ${theme.colors.neutral[200]}`,
                              borderBottom: `1px solid ${theme.colors.neutral[200]}`,
                              color: theme.colors.neutral[600],
                            }}
                          >
                            {t('dataset_upload.unknown_building', { id: bid.slice(0, 8) })}
                          </div>
                          <Table hover responsive className="mb-0">
                            <thead style={{ backgroundColor: theme.colors.neutral[100] }}>
                              <tr>
                                <th>{t('dataset_upload.col_name')}</th>
                                <th>{t('dataset_upload.col_type')}</th>
                                <th>{t('dataset_upload.col_date_range')}</th>
                                <th>{t('dataset_upload.col_resolution')}</th>
                                <th className="text-end">{t('dataset_upload.col_rows')}</th>
                                <th>{t('dataset_upload.col_status')}</th>
                                <th className="text-end">{t('dataset_upload.col_actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {orphans.map(renderDatasetRow)}
                            </tbody>
                          </Table>
                        </div>
                      );
                    })}
                </>
              )}
            </Card.Body>
          </Card>

          {/* ============================================================= */}
          {/* Summary footer                                                  */}
          {/* ============================================================= */}
          {datasets.length > 0 && (
            <Card className="shadow-sm mb-4">
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <div className="small text-muted">{t('dataset_upload.summary_total')}</div>
                    <div className="fw-bold fs-5">{datasets.length}</div>
                  </Col>
                  <Col md={3}>
                    <div className="small text-muted">{t('dataset_upload.summary_community')}</div>
                    <div className="fw-bold fs-5">{communityDatasets.length}</div>
                  </Col>
                  <Col md={3}>
                    <div className="small text-muted">{t('dataset_upload.summary_building')}</div>
                    <div className="fw-bold fs-5">{buildingDatasets.length}</div>
                  </Col>
                  <Col md={3}>
                    <div className="small text-muted">{t('dataset_upload.summary_with_data')}</div>
                    <div className="fw-bold fs-5">
                      {Object.keys(buildingDatasetsByBuilding).length} / {buildings.length}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}
        </>
      )}

      {/* =================================================================== */}
      {/* Upload Modal                                                         */}
      {/* =================================================================== */}
      <Modal
        show={showUploadModal}
        onHide={closeUploadModal}
        size="lg"
        backdrop="static"
        centered
      >
        <Modal.Header closeButton style={{ backgroundColor: theme.colors.primary.pale }}>
          <Modal.Title style={{ color: theme.colors.primary.dark }}>
            {uploadSection === 'community' ? t('dataset_upload.modal_upload_community') : t('dataset_upload.modal_upload_building')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Step 1: Select dataset type */}
          <Form.Group className="mb-3">
            <Form.Label><strong>{t('dataset_upload.field_dataset_type')}</strong> <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setUploadedFile(null);
                setValidationReport(null);
              }}
              style={{ borderColor: theme.colors.primary.main }}
            >
              <option value="">{t('dataset_upload.select_dataset_type')}</option>
              {availableTypes.map((dt) => (
                <option key={dt.type} value={dt.type}>
                  {dt.icon} {dt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Step 1b: For building datasets, select building */}
          {uploadSection === 'building' && (
            <Form.Group className="mb-3">
              <Form.Label><strong>{t('dataset_upload.field_building')}</strong> <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={selectedBuildingId}
                onChange={(e) => setSelectedBuildingId(e.target.value)}
                style={{ borderColor: theme.colors.primary.main }}
              >
                <option value="">{t('dataset_upload.select_building')}</option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.building_id} ({b.building_type})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {t('dataset_upload.building_help')}
              </Form.Text>
            </Form.Group>
          )}

          {/* Show dataset type info */}
          {selectedType && (
            <Alert variant="info" className="mb-3">
              {(() => {
                const typeInfo = availableTypes.find((dt) => dt.type === selectedType);
                if (!typeInfo) return null;
                return (
                  <div>
                    <div className="d-flex align-items-center mb-1">
                      <span style={{ fontSize: '1.5rem' }} className="me-2">{typeInfo.icon}</span>
                      <strong>{typeInfo.label}</strong>
                    </div>
                    <p className="small mb-2">{typeInfo.description}</p>
                    <div className="small">
                      <strong>{t('dataset_upload.required_columns')}</strong>{' '}
                      {typeInfo.requiredColumns.map((col) => (
                        <Badge key={col} bg="primary" className="me-1">{col}</Badge>
                      ))}
                    </div>
                    {typeInfo.optionalColumns && typeInfo.optionalColumns.length > 0 && (
                      <div className="small mt-1">
                        <strong>{t('dataset_upload.optional_columns')}</strong>{' '}
                        {typeInfo.optionalColumns.map((col) => (
                          <Badge key={col} bg="secondary" className="me-1">{col}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="small mt-1 text-muted">
                      <strong>{t('dataset_upload.format_label')}</strong> {typeInfo.format}
                    </div>
                  </div>
                );
              })()}
            </Alert>
          )}

          {/* Step 2: File upload dropzone */}
          {selectedType && (uploadSection === 'community' || selectedBuildingId) && (
            <>
              {!uploadedFile ? (
                <div
                  {...getRootProps()}
                  className="rounded p-4 text-center mb-3"
                  style={{
                    borderColor: isDragActive
                      ? theme.colors.primary.main
                      : theme.colors.neutral[300],
                    backgroundColor: isDragActive
                      ? theme.colors.primary.pale
                      : theme.colors.neutral[50],
                    cursor: 'pointer',
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                  }}
                >
                  <input {...getInputProps()} />
                  <div style={{ fontSize: '2.5rem' }}>
                    {isDragActive ? '📥' : '📁'}
                  </div>
                  <h6 className="mt-2 mb-1">
                    {isDragActive
                      ? t('dataset_upload.drop_here')
                      : t('dataset_upload.drag_or_click')}
                  </h6>
                  <p className="text-muted small mb-2">{t('dataset_upload.csv_only')}</p>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    style={{ borderColor: theme.colors.primary.main, color: theme.colors.primary.main }}
                  >
                    {t('dataset_upload.browse_files')}
                  </Button>
                </div>
              ) : (
                <>
                  {/* File selected */}
                  <Alert variant="success" className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{t('dataset_upload.file_label')}</strong> {uploadedFile.name}
                        <div className="small text-muted">
                          {t('dataset_upload.file_size', { size: (uploadedFile.size / 1024).toFixed(1) })}
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setUploadedFile(null);
                          setValidationReport(null);
                        }}
                        disabled={isSaving}
                      >
                        {t('dataset_upload.btn_remove')}
                      </Button>
                    </div>
                  </Alert>

                  {/* Validate button */}
                  {!validationReport && !isValidating && (
                    <div className="text-center mb-3">
                      <Button
                        variant="primary"
                        onClick={handleValidate}
                        style={{ backgroundColor: theme.colors.primary.main, borderColor: theme.colors.primary.main }}
                      >
                        {t('dataset_upload.btn_validate')}
                      </Button>
                    </div>
                  )}

                  {/* Validating spinner */}
                  {isValidating && (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" style={{ color: theme.colors.primary.main }} />
                      <span className="ms-2 text-muted">{t('dataset_upload.validating')}</span>
                    </div>
                  )}

                  {/* Validation results */}
                  {validationReport && (
                    <Card className="mb-3 border">
                      <Card.Header className="d-flex justify-content-between align-items-center">
                        <span>
                          <Badge bg={validationReport.valid !== false ? 'success' : 'danger'} className="me-2">
                            {validationReport.valid !== false ? t('dataset_upload.valid') : t('dataset_upload.invalid')}
                          </Badge>
                          {t('dataset_upload.validation_report')}
                        </span>
                        <span className="small text-muted">
                          {t('dataset_upload.rows_count', { count: validationReport.row_count || 0 })}
                        </span>
                      </Card.Header>
                      <Card.Body className="small">
                        {/* Summary */}
                        <Row className="mb-2">
                          <Col xs={4}>
                            <div className="text-muted">{t('dataset_upload.summary_columns')}</div>
                            <div>{(validationReport.column_names || []).join(', ') || t('common.n_a')}</div>
                          </Col>
                          <Col xs={4}>
                            <div className="text-muted">{t('dataset_upload.summary_date_range')}</div>
                            <div>
                              {validationReport.date_range?.start
                                ? new Date(validationReport.date_range.start).toLocaleDateString()
                                : t('common.n_a')}
                              {' - '}
                              {validationReport.date_range?.end
                                ? new Date(validationReport.date_range.end).toLocaleDateString()
                                : t('common.n_a')}
                            </div>
                          </Col>
                          <Col xs={4}>
                            <div className="text-muted">{t('dataset_upload.summary_resolution')}</div>
                            <div>{validationReport.time_resolution || t('common.n_a')}</div>
                          </Col>
                        </Row>

                        {/* Errors */}
                        {validationReport.errors && validationReport.errors.length > 0 && (
                          <Alert variant="danger" className="mb-2 small py-2">
                            <strong>{t('dataset_upload.errors_count', { count: validationReport.errors.length })}</strong>
                            <ul className="mb-0 mt-1" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                              {validationReport.errors.slice(0, 10).map((e, i) => (
                                <li key={i}>
                                  {e.row ? t('dataset_upload.row_label', { row: e.row }) : t('dataset_upload.file_label_short')}
                                  {e.column ? ` / ${e.column}` : ''}
                                  : {e.message}
                                </li>
                              ))}
                              {validationReport.errors.length > 10 && (
                                <li className="text-muted">
                                  {t('dataset_upload.and_more', { count: validationReport.errors.length - 10 })}
                                </li>
                              )}
                            </ul>
                          </Alert>
                        )}

                        {/* Warnings */}
                        {validationReport.warnings && validationReport.warnings.length > 0 && (
                          <Alert variant="warning" className="mb-2 small py-2">
                            <strong>{t('dataset_upload.warnings_count', { count: validationReport.warnings.length })}</strong>
                            <ul className="mb-0 mt-1" style={{ maxHeight: '100px', overflowY: 'auto' }}>
                              {validationReport.warnings.slice(0, 10).map((w, i) => (
                                <li key={i}>{w.message}</li>
                              ))}
                              {validationReport.warnings.length > 10 && (
                                <li className="text-muted">
                                  {t('dataset_upload.and_more', { count: validationReport.warnings.length - 10 })}
                                </li>
                              )}
                            </ul>
                          </Alert>
                        )}

                        {/* Column stats */}
                        {validationReport.column_stats && Object.keys(validationReport.column_stats).length > 0 && (
                          <div className="mt-2">
                            <strong>{t('dataset_upload.column_stats')}</strong>
                            <Table size="sm" bordered className="mt-1 mb-0">
                              <thead style={{ backgroundColor: theme.colors.neutral[100] }}>
                                <tr>
                                  <th>{t('dataset_upload.stat_column')}</th>
                                  <th className="text-end">{t('dataset_upload.stat_min')}</th>
                                  <th className="text-end">{t('dataset_upload.stat_max')}</th>
                                  <th className="text-end">{t('dataset_upload.stat_mean')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(validationReport.column_stats).map(([col, s]) => (
                                  <tr key={col}>
                                    <td className="fw-semibold">{col}</td>
                                    <td className="text-end">{s.min != null ? Number(s.min).toFixed(2) : '--'}</td>
                                    <td className="text-end">{s.max != null ? Number(s.max).toFixed(2) : '--'}</td>
                                    <td className="text-end">{s.mean != null ? Number(s.mean).toFixed(2) : '--'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}

                        {/* Sample rows */}
                        {validationReport.sample_rows && validationReport.sample_rows.length > 0 && (
                          <div className="mt-2">
                            <strong>{t('dataset_upload.sample_rows')}</strong>
                            <div className="table-responsive mt-1">
                              <Table size="sm" bordered className="mb-0">
                                <thead style={{ backgroundColor: theme.colors.neutral[100] }}>
                                  <tr>
                                    {Object.keys(validationReport.sample_rows[0]).map((k) => (
                                      <th key={k}>{k}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {validationReport.sample_rows.slice(0, 5).map((row, i) => (
                                    <tr key={i}>
                                      {Object.values(row).map((v, j) => (
                                        <td key={j}>{v != null ? String(v) : ''}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Success message */}
                        {validationReport.valid !== false && validationReport.errors?.length === 0 && (
                          <Alert variant="success" className="mt-2 mb-0 py-2">
                            {t('dataset_upload.validation_success')}
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  )}
                </>
              )}
            </>
          )}

          {/* Hint for building datasets when building not selected */}
          {uploadSection === 'building' && selectedType && !selectedBuildingId && (
            <Alert variant="info" className="mb-0">
              {t('dataset_upload.building_required_hint')}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeUploadModal} disabled={isSaving}>
            {t('dataset_upload.btn_cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            style={{ backgroundColor: theme.colors.primary.main, borderColor: theme.colors.primary.main }}
            disabled={
              isSaving ||
              isValidating ||
              !uploadedFile ||
              !selectedType ||
              !validationReport ||
              validationReport.valid === false ||
              (uploadSection === 'building' && !selectedBuildingId)
            }
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('dataset_upload.btn_saving')}
              </>
            ) : (
              t('dataset_upload.btn_save')
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* =================================================================== */}
      {/* Delete Confirmation Modal                                            */}
      {/* =================================================================== */}
      <Modal
        show={!!deleteTarget}
        onHide={() => !isDeleting && setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('dataset_upload.confirm_delete')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget && (
            <p>{t('dataset_upload.delete_warning', { name: deleteTarget.name })}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setDeleteTarget(null)}
            disabled={isDeleting}
          >
            {t('dataset_upload.btn_cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {t('dataset_upload.btn_deleting')}
              </>
            ) : (
              t('dataset_upload.btn_delete_confirm')
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DatasetUploadPage;
